import React from "react";
import { Link } from "../../components/PageLink";
import { Helmet } from "react-helmet-async";
import CTABand from "../../components/CTABand";
import Picture, { type ImgSet } from "../../components/Picture";
import ImageStrip, { type StripItem } from "../../components/ImageStrip";
import Testimonials from "../../components/Testimonials";
import { supplierImg } from "../../lib/supplierImages";
import { CLUSTERS, SERVICES } from "../../lib/areas";

interface ClusterPageProps {
  clusterName: string;
  cities: string[];
  primaryImageBase?: string;
  primaryImageAlt?: string;
  stripItems?: StripItem[];
  projectImageBase?: string;
  projectAlt?: string;
  projectHeading?: string;
  projectBody?: string;
  projectResponsiveImage?: boolean;
}

const formatList = (items: string[]) => {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

const ClusterPage: React.FC<ClusterPageProps> = ({
  clusterName,
  cities,
  primaryImageBase = "j-s-audio-visual-solutions-living-room-tv-wall-installation",
  primaryImageAlt,
  stripItems,
  projectImageBase,
  projectAlt,
  projectHeading,
  projectBody,
  projectResponsiveImage = false,
}) => {
  const cityList = formatList(cities);
  const slug = clusterName.toLowerCase().replace(/[^a-z]+/g, "-");
  const primaryImage: ImgSet = supplierImg(primaryImageBase);
  const cluster = CLUSTERS.find((c) => c.name === clusterName);
  const blurb = cluster?.blurb;

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `Audio Visual Installation in the ${clusterName}`,
    serviceType: "Audio Visual Installation",
    description: `Installation services, audio visual equipment supply, and technical support for ${cityList} and the surrounding ${clusterName} of Central Massachusetts.`,
    provider: { "@id": "https://jandsav.com/#localbusiness" },
    areaServed: cities,
  };

  return (
    <>
      <Helmet>
        <title>{`J&S Audio Video Solutions – ${clusterName} – Central MA`}</title>
        <meta
          name="description"
          content={cluster?.metaDescription ?? `Audio visual installation services and technical support across the ${clusterName} of Central Massachusetts, covering ${cityList}, by J&S Audio Video Solutions.`}
        />
        <meta property="og:title" content={`J&S Audio Video Solutions – ${clusterName}`} />
        <meta property="og:description" content={`AV installation and supply across ${cityList}.`} />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      <section className="subhero" data-testid={`subhero-${slug}`}>
        <div className="container">
          <span className="eyebrow" style={{ color: "var(--color-accent)" }}>Service Area</span>
          <h1>Audio Visual Services in the {clusterName}</h1>
          <p className="lead">
            J&amp;S Audio Video Solutions covers the {clusterName} of Central Massachusetts, including {cityList}. We
            handle installation services, audio visual equipment supply, and ongoing technical support for homes,
            restaurants, offices, and event venues across the region.
          </p>
          <div className="hero-ctas" style={{ marginTop: "var(--space-4)" }}>
            <Link to="/contact" className="btn" data-testid="link-cluster-cta">Request a Consultation</Link>
            <a href="tel:+15084500490" className="btn btn-outline" data-testid="link-cluster-phone">Call 508-450-0490</a>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <span className="eyebrow">What we do here</span>
          <h2>Three core practices, available to every {clusterName} project.</h2>
          <p className="lead" style={{ maxWidth: 760, marginTop: "0.5rem" }}>
            Whether you're a homeowner planning a home theater, a restaurant operator rebuilding the audio, or a corporate
            facilities lead specifying a conference room. The same team that designs it also installs it and supports it.
          </p>

          <div className="card-grid card-grid-3 mt-5">
            {SERVICES.map((s) => (
              <article className="card" key={s.slug} data-testid={`link-cluster-service-${s.slug}`}>
                <div className="card-content">
                  <h3 className="card-title">{s.title}</h3>
                  <p className="card-desc">{s.short}</p>
                  <Link to={s.path} className="card-link">Explore {s.title}</Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-light">
        <div className="container split">
          <div>
            <Picture
              src={primaryImage}
              alt={primaryImageAlt ?? `Audio visual installation in ${cities[0]}, MA by J&S Audio Video Solutions`}
              width={1200}
              height={900}
              className="split-img"
              sizes="(max-width: 768px) 100vw, 600px"
            />
          </div>
          <div>
            <span className="eyebrow">Local, not generic</span>
            <h2>Working in the {clusterName} since the beginning.</h2>
            <p style={{ marginTop: "1rem" }}>{blurb}</p>
          </div>
        </div>
      </section>

      {stripItems && stripItems.length > 0 && (
        <section className="section" data-testid={`cluster-strip-${slug}`}>
          <div className="container">
            <span className="eyebrow">Recent {clusterName} work</span>
            <h2>The variety of jobs we run in {cities[0]} and nearby.</h2>
            <ImageStrip items={stripItems} testId={`strip-${slug}`} />
          </div>
        </section>
      )}

      {projectHeading && projectImageBase && (
        <section className="section section-light" data-testid={`cluster-project-${slug}`}>
          <div className="container split">
            <div>
              <Picture
                src={supplierImg(projectImageBase)}
                alt={projectAlt ?? projectHeading}
                width={1200}
                height={900}
                className="split-img"
                sizes="(max-width: 768px) 100vw, 600px"
                responsiveVariantByViewport={projectResponsiveImage}
              />
            </div>
            <div>
              <span className="eyebrow">Recent project</span>
              <h2>{projectHeading}</h2>
              <p style={{ marginTop: "1rem" }}>{projectBody}</p>
            </div>
          </div>
        </section>
      )}

      <Testimonials />

      <CTABand
        heading={`Planning a project in the ${clusterName}?`}
        body="Tell us what you're trying to build. We'll come back with a clean, honest plan."
      />
    </>
  );
};

export default ClusterPage;
